import { escapeHtml } from './markdown.js';
import { HelpRegistry } from './help-registry.js';
import { HELP_ENTRY_STYLES, renderEntryBody, renderDraftBadge } from './help-render.js';

const MODAL_STYLES = `
:host { display: contents; }

.hm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1300;
    display: flex;
    align-items: flex-start;
    justify-content: center;
}

.hm-dialog {
    position: relative;
    top: 60px;
    width: min(560px, calc(100% - 40px));
    max-height: calc(100% - 120px);
    background: var(--bg-primary, #fff);
    border-radius: 10px;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.hm-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 14px 18px;
    border-bottom: 1px solid var(--border-color, #e8e8e8);
}

.hm-titles {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
}

.hm-back {
    all: unset;
    cursor: pointer;
    font-size: 13px;
    color: var(--accent-color, #0066cc);
    padding: 2px 6px;
    flex-shrink: 0;
}
.hm-back:hover { text-decoration: underline; }

.hm-title {
    font-size: 14px;
    font-weight: 700;
    font-family: monospace;
    color: var(--text-primary, #222);
}

.hm-crumb-sep { color: var(--text-secondary, #aaa); font-size: 12px; }

.hm-close {
    border: none;
    background: none;
    font-size: 18px;
    cursor: pointer;
    color: var(--text-secondary, #888);
    padding: 2px 6px;
    flex-shrink: 0;
}

.hm-body {
    padding: 14px 18px;
    overflow: auto;
    font-size: 13px;
    line-height: 1.5;
    color: var(--text-primary, #222);
}
`;

// Modal for a help entry's "long" content. Plain HTMLElement (not HSComponent),
// same precedent as HSListPicker: created on demand and appended into the
// caller's shadow root, torn down (removed) on close.
//
// Tier-2 addition: `see_also` links navigate in-place (breadcrumb + back),
// rather than requiring the modal to be closed and reopened per hop.
export class HSHelpModal extends HTMLElement {
    #root;
    #stack = [];   // [{ key, entry }, ...] — visited chain, last = current
    #onKeydown = null;

    constructor() {
        super();
        this.#root = this.attachShadow({ mode: 'open' });
        const modalSheet = new CSSStyleSheet();
        modalSheet.replaceSync(MODAL_STYLES);
        const entrySheet = new CSSStyleSheet();
        entrySheet.replaceSync(HELP_ENTRY_STYLES);
        this.#root.adoptedStyleSheets = [modalSheet, entrySheet];
    }

    open({ key, entry }) {
        if (!entry?.long) return;
        this.#stack = [{ key, entry }];
        this.#buildShell();
        this.#renderCurrent();
    }

    #buildShell() {
        if (this.#root.querySelector('.hm-overlay')) return;

        this.#root.innerHTML = `
            <div class="hm-overlay">
                <div class="hm-dialog">
                    <div class="hm-header">
                        <div class="hm-titles"></div>
                        <button class="hm-close" aria-label="Close">✕</button>
                    </div>
                    <div class="hm-body"></div>
                </div>
            </div>
        `;

        const overlay = this.#root.querySelector('.hm-overlay');
        this.#onKeydown = (ev) => { if (ev.key === 'Escape') this.#close(); };
        document.addEventListener('keydown', this.#onKeydown);
        this.#root.querySelector('.hm-close').addEventListener('click', () => this.#close());
        overlay.addEventListener('click', (ev) => { if (ev.target === overlay) this.#close(); });
        this.#root.querySelector('.hm-body').addEventListener('click', (ev) => {
            const link = ev.target.closest('.he-seealso-link');
            if (link) this.#navigateTo(link.dataset.helpNavKey);
        });
        this.#root.querySelector('.hm-titles').addEventListener('click', (ev) => {
            const crumb = ev.target.closest('[data-crumb-index]');
            if (crumb) this.#goToCrumb(Number(crumb.dataset.crumbIndex));
        });
    }

    #navigateTo(key) {
        const entry = HelpRegistry.getEntry(key);
        if (!entry) return;   // unresolvable — skip silently
        this.#stack.push({ key, entry });
        this.#renderCurrent();
    }

    #goToCrumb(index) {
        if (index < 0 || index >= this.#stack.length - 1) return;
        this.#stack = this.#stack.slice(0, index + 1);
        this.#renderCurrent();
    }

    #renderCurrent() {
        const { entry } = this.#stack[this.#stack.length - 1];

        const crumbs = this.#stack.map(({ key: k }, i) => {
            const isLast = i === this.#stack.length - 1;
            return isLast
                ? `<span class="hm-title">${escapeHtml(k)}</span>`
                : `<button type="button" class="hm-back" data-crumb-index="${i}">${escapeHtml(k)}</button><span class="hm-crumb-sep">›</span>`;
        }).join('');
        this.#root.querySelector('.hm-titles').innerHTML = `${crumbs}${renderDraftBadge(entry)}`;
        this.#root.querySelector('.hm-body').innerHTML = renderEntryBody(entry);
    }

    #close() {
        if (this.#onKeydown) document.removeEventListener('keydown', this.#onKeydown);
        this.remove();
    }
}

customElements.define('hs-help-modal', HSHelpModal);

